/** Base URL without trailing slash (same convention as `NEXT_PUBLIC_API_URL` for `api`). */
import type { AdoptionQuizThemeMode } from '../../../theme/shelterPaletteCatalog';

function apiBase(): string {
  return (process.env.NEXT_PUBLIC_API_URL ?? '').replace(/\/+$/, '');
}

export function publicSheltersApiUrl(path: string): string {
  const b = apiBase();
  const p = path.startsWith('/') ? path : `/${path}`;
  return b ? `${b}${p}` : p;
}

export type PublicShelterListItemDto = {
  id: string;
  name: string;
  slug: string | null;
  /** When set, public `/adopt/...` quiz applies these `--theme-*` variables only under the quiz subtree. */
  adoptionQuizTheme: {
    paletteId: string;
    mode: AdoptionQuizThemeMode;
  } | null;
};

/** `GET /shelters/public/{segment}` — includes optional signed shelter logo URL. */
export type PublicShelterLookupDto = PublicShelterListItemDto & {
  logoSignedUrl: string | null;
  /** Valid http(s) shelter website when configured. */
  shelterUrl: string | null;
  /** Valid http(s) external adoption application form when configured. */
  shelterAdoptionFormUrl: string | null;
};

type ApiSuccess<T> = {
  success: true;
  data: T;
  timestamp: string;
};

export async function fetchPublicSheltersList(): Promise<{
  shelters: PublicShelterListItemDto[];
  count: number;
}> {
  const res = await fetch(publicSheltersApiUrl('/shelters/public'), {
    method: 'GET',
    headers: { Accept: 'application/json' },
    cache: 'no-store',
  });
  if (!res.ok) {
    throw new Error(`Public shelters list failed (${res.status})`);
  }
  const json = (await res.json()) as ApiSuccess<{
    shelters: PublicShelterListItemDto[];
    count: number;
  }>;
  return json.data;
}

export async function fetchPublicShelterLookup(
  segment: string,
): Promise<PublicShelterLookupDto> {
  const res = await fetch(
    publicSheltersApiUrl(
      `/shelters/public/${encodeURIComponent(segment)}`,
    ),
    {
      method: 'GET',
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    },
  );
  if (!res.ok) {
    throw new Error(`Public shelter lookup failed (${res.status})`);
  }
  const json = (await res.json()) as ApiSuccess<PublicShelterLookupDto>;
  return json.data;
}
